import React, { useState } from 'react';
import { Phone, Mail, MessageCircle, Paperclip, ExternalLink, Copy, Check, UserPlus } from 'lucide-react';
import { MdOutlineEdit } from 'react-icons/md';
import moment from 'moment';
import logger, { Logger } from '../../../utils/logger';
import ContactLinksGenerator from '../../../utils/contactlinksgenerator';
import './LeadInfoPanel.css'; // Import the CSS file

interface LeadInfoPanelProps {
  lead: {
    name: string;
    email: string;
    phone: string;
    status: string[];
    labels: Array<{value: string; color?: string}>;
    createdAt: Date;
    leadSource: string;
    createdBy: string;
    assignedTo: string;
    list: string;
    saleValue?: string;
  };
  onStatusEdit: () => void;
  onLabelEdit: () => void;

  onAssignClick?: () => void; // ADD THIS
  getEmployeeInfoById?: (id: string) => string; // ADD THIS

}

const LeadInfoPanel: React.FC<LeadInfoPanelProps> = ({
  lead,
  onStatusEdit,
  onLabelEdit,

 
   onAssignClick, 
  getEmployeeInfoById, 
  
}) => {
  // State for managing copy feedback
  const [copiedField, setCopiedField] = useState<string | null>(null);

  /**
   * Copy text to clipboard and show feedback
   * @param text - Text to copy
   * @param fieldType - Type of field being copied (for feedback)
   */
  const copyToClipboard = async (text: string, fieldType: 'phone' | 'email') => {
    try {
      logger.log(`📋 Copying ${fieldType}:`, text);
      
      // Use the Clipboard API to copy text
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldType);
      
      // Show toast notification with multiple fallback methods
      const showToast = (message: string) => {
        logger.log('🍞 Attempting to show toast:', message);
        
        // Try different toast implementations in order of preference
        if (typeof window !== 'undefined') {
          // React Hot Toast (most common in modern React apps)
          if ((window as any).toast?.success) {
            logger.log('✅ Using react-hot-toast');
            (window as any).toast.success(message);
            return true;
          }
          
          // Toastr (classic toast library)
          if ((window as any).toastr?.success) {
            logger.log('✅ Using toastr');
            (window as any).toastr.success(message);
            return true;
          }
          
          // Custom toast function (if app has one)
          if ((window as any).showToast) {
            logger.log('✅ Using custom showToast');
            (window as any).showToast(message);
            return true;
          }
          
          // Ant Design notification (if available)
          if ((window as any).antd?.notification?.success) {
            logger.log('✅ Using Ant Design notification');
            (window as any).antd.notification.success({
              message: 'Copied!',
              description: message,
            });
            return true;
          }
        }
        
        // Fallback: Log to console (you can uncomment alert if needed)
        logger.log('📢 Toast fallback - using console log:', message);
        // alert(message); // Uncomment for alert fallback
        return false;
      };
      
      const message = `${fieldType.charAt(0).toUpperCase() + fieldType.slice(1)} copied to clipboard!`;
      showToast(message);
      
      // Reset copied state after 2 seconds with visual feedback
      setTimeout(() => {
        logger.log(`✅ Resetting copy state for ${fieldType}`);
        setCopiedField(null);
      }, 2000);
      
    } catch (err) {
      console.error('❌ Failed to copy text:', err);
      const errorMessage = `Failed to copy ${fieldType}`;
      
      // Try to show error toast
      if (typeof window !== 'undefined') {
        if ((window as any).toast?.error) {
          (window as any).toast.error(errorMessage);
        } else if ((window as any).toastr?.error) {
          (window as any).toastr.error(errorMessage);
        } else {
          logger.log('📢 Copy failed - fallback message:', errorMessage);
          // alert(errorMessage); // Uncomment for alert fallback
        }
      }
    }
  };

  logger.log('🎨 LeadInfoPanel render with lead:', lead.name);

  return (
    <div className="lead-info-panel">
      {/* Lead Information - maintains existing structure but adds copy functionality */}
      <ul className="popup-section-4-left-2">
        {/* Enhanced Phone Field with Copy Functionality */}
        {lead.phone && (
          <li>
            <div className="copy-field-container">
              <div className="copy-field-content">
                <span>Phone</span>
                <div className="copy-field-value">{lead.phone}</div>
              </div>
              <button
                className={`copy-btn ${copiedField === 'phone' ? 'copied' : ''}`}
                onClick={() => copyToClipboard(lead.phone, 'phone')}
                title="Copy phone number"
                aria-label="Copy phone number to clipboard"
              >
                {copiedField === 'phone' ? (
                  <Check size={14} />
                ) : (
                  <Copy size={14} />
                )}
              </button>
            </div>
          </li>
        )}

        {/* Enhanced Email Field with Copy Functionality */}
        {lead.email && (
          <li>
            <div className="copy-field-container">
              <div className="copy-field-content">
                <span>Email</span>
                <div className="copy-field-value">{lead.email}</div>
              </div>
              <button
                className={`copy-btn ${copiedField === 'email' ? 'copied' : ''}`}
                onClick={() => copyToClipboard(lead.email, 'email')}
                title="Copy email address"
                aria-label="Copy email address to clipboard"
              >
                {copiedField === 'email' ? (
                  <Check size={14} />
                ) : (
                  <Copy size={14} />
                )}
              </button>
            </div>
          </li>
        )}

        {/* Enhanced Status Field with Edit Functionality */}
        <li>
          <div className="editable-field-header">
            <span>Status</span>
            <button
              className="edit-btn"
              onClick={onStatusEdit}
              title="Edit status"
              aria-label="Edit lead status"
            >
              <MdOutlineEdit size={12} />
            </button>
          </div>
          <div className="badge-container">
            {lead.status?.length > 0 ? (
              lead.status.map((status, index) => (
                <span key={index} className="status-badge">
                  {status}
                </span>
              ))
            ) : (
              <span className="copy-field-value">No status assigned</span>
            )}
          </div>
        </li>

        {/* Enhanced Label Field with Edit Functionality */}
        <li>
          <div className="editable-field-header">
            <span>Label</span>
            <button
              className="edit-btn"
              onClick={onLabelEdit}
              title="Edit labels"
              aria-label="Edit lead labels"
            >
              <MdOutlineEdit size={12} />
            </button>
          </div>
          <div className="badge-container">
            {lead.labels?.length > 0 ? (
              lead.labels.map((label, index) => (
                <span key={index} className="label-badge">
                  {label.value}
                </span>
              ))
            ) : (
              <span className="copy-field-value">No labels assigned</span>
            )}
          </div>
        </li>

      {/* Date Added Field */}
        <li className="standard-field">
          <span>Date added</span>
          <div className="standard-field-value">
            {moment(lead.createdAt).format("DD MMM YYYY HH:mm A")}
          </div>
        </li>

        {/* Lead Source Field */}
        <li className="standard-field">
          <span>Lead Source</span>
          <div className="standard-field-value">{lead.leadSource}</div>
        </li>

        {/* Created By Field */}
        <li className="standard-field">
          <span>Created by</span>
          <div className="standard-field-value">{lead.createdBy}</div>
        </li>

        {/* Assigned To Field */}
       <li>
  <div 
    className="editable-field-header cursor-pointer"
    onClick={onAssignClick}
    style={{
      padding: '6px 8px',
      borderRadius: '6px',
      border: '1px solid rgba(0,0,0,0.05)',
      backgroundColor: 'rgba(255,255,255,0.5)',
      transition: 'all 0.2s ease'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.8)';
      e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.5)';
      e.currentTarget.style.borderColor = 'rgba(0,0,0,0.05)';
    }}
    title="Click to assign/reassign this lead"
  >
    <div className="d-flex justify-content-between align-items-center">
      <span>Assigned to</span>
      <UserPlus size={12} />
    </div>
  </div>
  <div className="standard-field-value" style={{ marginTop: '4px', paddingLeft: '8px' }}>
    {getEmployeeInfoById ? getEmployeeInfoById(lead.assignedTo) : (lead.assignedTo || "Click to assign")}
  </div>
</li>

        {/* List Field (conditional) */}
        {lead.list && (
          <li className="standard-field">
            <span>List</span>
            <div className="standard-field-value">{lead.list}</div>
          </li>
        )}
      </ul>
    </div>
  );
};

export default LeadInfoPanel;